import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, RefreshCw, Save, ShieldAlert, ChevronDown, ChevronRight, GitBranch } from "lucide-react";
import { toast } from "sonner";

const API = window.location.origin;

/**
 * StagePermissionsPanel — matrice di permessi (VISIONE / MODIFICA / FOTO)
 * per stage GHL applicata trasversalmente agli utenti con role=user
 * oppure role=user_plus (matrice separata, selezionabile via tab).
 *
 * Regole:
 *  - MODIFICA o FOTO → auto-check di VISIONE (cascade up)
 *  - Toglieere VISIONE → toglie anche MODIFICA e FOTO (cascade down)
 *  - Default stage nuovo: solo VISIONE ON (gestito dal backend)
 *  - Click su opp non-visibile: lista mostra l'opp ma click → "Permesso
 *    negato" (la lista non è filtrata, l'enforcement è all'apertura)
 *
 * Backend: GET/PUT /api/admin/stage-permissions?audience=user|user_plus
 */
export default function StagePermissionsPanel() {
  const [audience, setAudience] = useState("user"); // 'user' | 'user_plus'
  const [data, setData] = useState({ pipelines: [], updated_at: null, updated_by: "" });
  const [matrix, setMatrix] = useState({});            // pipeline_id → stage_id → {vision,edit,photo}
  const [originalMatrix, setOriginalMatrix] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState({});         // pipeline_id → bool

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/stage-permissions`, {
        params: { audience },
        withCredentials: true,
      });
      const payload = res.data || { pipelines: [] };
      // Costruisce la matrice in memoria a partire dalla response.
      // Ogni stage ha già i 3 flag valorizzati (default applicato dal BE).
      const m = {};
      const e = {};
      for (const p of payload.pipelines || []) {
        m[p.id] = {};
        e[p.id] = true; // default expanded
        for (const s of p.stages || []) {
          m[p.id][s.id] = {
            vision: !!s.vision,
            edit: !!s.edit,
            photo: !!s.photo,
          };
        }
      }
      setData(payload);
      setMatrix(m);
      setOriginalMatrix(JSON.parse(JSON.stringify(m)));
      setExpanded(e);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel caricamento dei permessi stage");
    } finally {
      setLoading(false);
    }
  }, [audience]);

  useEffect(() => { load(); }, [load]);

  const isDirty = JSON.stringify(matrix) !== JSON.stringify(originalMatrix);

  // Pipeline Promemoria (Reparto Clinico / Ortodonzia / legacy): gli
  // stage rappresentano i mesi dell'anno (Gennaio, Febbraio, ...). Per
  // semplificare la gestione, mostriamo una sola riga "Tutti i mesi"
  // che applica le stesse 3 flag a TUTTI gli stage della pipeline.
  // Richiesta utente 06/06/2026.
  const isPromemoriaPipeline = (pipelineName) => {
    return /promemoria/i.test(pipelineName || "");
  };

  // Per le pipeline Promemoria, calcola i flag "consensus" dagli stage
  // della pipeline (priorità all'ultimo stato salvato; se inconsistenti,
  // usa il primo stage come rappresentativo).
  const getPromemoriaConsensus = (pipelineId, pipelineStages) => {
    const flags = (pipelineStages || []).map((s) => matrix[pipelineId]?.[s.id] || { vision: false, edit: false, photo: false });
    if (flags.length === 0) return { vision: false, edit: false, photo: false };
    // Usa il primo stage come "verità". Quando l'admin toggla, propaghiamo
    // a tutti gli stage, quindi alla prossima render saranno tutti allineati.
    return flags[0];
  };

  // Toggle "Tutti i mesi" → applica le regole V↔M esclusione e F→V autocheck
  // a TUTTI gli stage della pipeline simultaneamente.
  const togglePromemoriaFlag = (pipelineId, pipelineStages, flag) => {
    setMatrix((prev) => {
      const next = { ...prev };
      next[pipelineId] = { ...(next[pipelineId] || {}) };
      // Calcola il nuovo stato basandosi sul primo stage (consensus)
      const current = getPromemoriaConsensus(pipelineId, pipelineStages);
      const updated = { ...current };
      if (flag === "vision") {
        updated.vision = !updated.vision;
        if (updated.vision) updated.edit = false;
        if (!updated.vision && !updated.edit) updated.photo = false;
      } else if (flag === "edit") {
        updated.edit = !updated.edit;
        if (updated.edit) updated.vision = false;
        if (!updated.edit && !updated.vision) updated.photo = false;
      } else if (flag === "photo") {
        updated.photo = !updated.photo;
        if (updated.photo && !updated.vision && !updated.edit) {
          updated.vision = true;
        }
      }
      // Propaga a tutti gli stage della pipeline
      for (const s of pipelineStages || []) {
        next[pipelineId][s.id] = { ...updated };
      }
      return next;
    });
  };

  // Toggle un singolo flag con regole:
  //  - VISIONE ↔ MODIFICA: mutuamente esclusivi (selezionare uno deseleziona l'altro)
  //  - FOTO: indipendente. Se selezionato mentre V e M sono entrambi off,
  //    auto-check VISIONE per garantire che l'utente possa almeno aprire l'opp.
  //  - Se VISIONE viene tolta (e M è off): non tocca F. Ma se entrambi V e M
  //    diventano off mentre F è attivo, F resta solo se ce n'è ancora uno tra V/M.
  //    In pratica: F può essere on solo se almeno V o M è on.
  const toggleFlag = (pipelineId, stageId, flag) => {
    setMatrix((prev) => {
      const next = { ...prev };
      next[pipelineId] = { ...(next[pipelineId] || {}) };
      const current = next[pipelineId][stageId] || { vision: false, edit: false, photo: false };
      const updated = { ...current };
      if (flag === "vision") {
        updated.vision = !updated.vision;
        if (updated.vision) updated.edit = false; // V e M mutuamente esclusivi
        if (!updated.vision && !updated.edit) updated.photo = false; // F richiede V o M
      } else if (flag === "edit") {
        updated.edit = !updated.edit;
        if (updated.edit) updated.vision = false; // V e M mutuamente esclusivi
        if (!updated.edit && !updated.vision) updated.photo = false; // F richiede V o M
      } else if (flag === "photo") {
        updated.photo = !updated.photo;
        // F auto-checka V se né V né M sono attivi
        if (updated.photo && !updated.vision && !updated.edit) {
          updated.vision = true;
        }
      }
      next[pipelineId][stageId] = updated;
      return next;
    });
  };

  // Bottoni rapidi per intera pipeline
  const applyPipelinePreset = (pipelineId, preset) => {
    const pipeline = data.pipelines.find((p) => p.id === pipelineId);
    if (!pipeline) return;
    setMatrix((prev) => {
      const next = { ...prev };
      next[pipelineId] = { ...(next[pipelineId] || {}) };
      for (const s of pipeline.stages || []) {
        if (preset === "all") next[pipelineId][s.id] = { vision: false, edit: true, photo: true };
        else if (preset === "vision") next[pipelineId][s.id] = { vision: true, edit: false, photo: false };
        else next[pipelineId][s.id] = { vision: false, edit: false, photo: false };
      }
      return next;
    });
  };

  const toggleExpanded = (pipelineId) => {
    setExpanded((prev) => ({ ...prev, [pipelineId]: !prev[pipelineId] }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/api/admin/stage-permissions`,
        { permissions: matrix },
        { params: { audience }, withCredentials: true }
      );
      // Backend normalizza (cascade up) — risincronizza la UI col risultato.
      const normalized = res.data?.permissions || matrix;
      const m = {};
      for (const pid of Object.keys(normalized)) {
        m[pid] = {};
        for (const sid of Object.keys(normalized[pid] || {})) {
          m[pid][sid] = {
            vision: !!normalized[pid][sid]?.vision,
            edit: !!normalized[pid][sid]?.edit,
            photo: !!normalized[pid][sid]?.photo,
          };
        }
      }
      // Merge: per stage non presenti nella response (default), conserva matrix corrente
      const merged = { ...matrix, ...m };
      setMatrix(merged);
      setOriginalMatrix(JSON.parse(JSON.stringify(merged)));
      setData((prev) => ({ ...prev, updated_at: res.data?.updated_at, updated_by: res.data?.updated_by }));
      const targetLabel = audience === "user_plus" ? "USER+" : "USER";
      toast.success(`Permessi stage salvati per ${targetLabel}. Si applicheranno entro 60s su tutte le sessioni.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#3B42DD]" />
      </div>
    );
  }

  const totalStages = data.pipelines.reduce((sum, p) => sum + (p.stages?.length || 0), 0);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="stage-permissions-panel">
      <div className="px-5 py-4 bg-gradient-to-r from-indigo-50 to-blue-50 border-b border-slate-200">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center flex-shrink-0">
              <ShieldAlert className="w-5 h-5 text-indigo-600" />
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-bold text-slate-800">
                Permessi {audience === "user_plus" ? "USER+" : "USER"} per stage GHL
              </h3>
              <p className="text-[11px] text-slate-500 mt-0.5">
                Matrice trasversale: si applica a tutti gli utenti con ruolo{" "}
                <strong>{audience === "user_plus" ? "user_plus" : "user"}</strong>.
                Gli admin hanno sempre accesso completo. Le due matrici (USER e USER+) sono indipendenti.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={load}
              disabled={loading || saving}
              className="rounded-xl border-slate-200 text-slate-600 hover:bg-slate-50"
              data-testid="stage-permissions-refresh"
              title="Ricarica pipeline/stage da GHL"
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
              Aggiorna da GHL
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleSave}
              disabled={!isDirty || saving}
              className="rounded-xl bg-[#3B42DD] hover:bg-[#2f36b8] text-white disabled:opacity-40"
              data-testid="stage-permissions-save"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
              {isDirty ? "Salva permessi" : "Nessuna modifica"}
            </Button>
          </div>
        </div>
        {/* Tab switch USER / USER+ */}
        <div className="mt-3 inline-flex bg-white/70 backdrop-blur rounded-xl p-1 border border-slate-200/60 gap-1">
          <button
            type="button"
            onClick={() => {
              if (isDirty) {
                if (!window.confirm("Hai modifiche non salvate. Vuoi davvero cambiare tab e perderle?")) return;
              }
              setAudience("user");
            }}
            className={`px-3 py-1.5 text-[11px] font-semibold rounded-lg transition-colors ${
              audience === "user"
                ? "bg-indigo-600 text-white shadow-sm"
                : "text-slate-600 hover:bg-slate-50"
            }`}
            data-testid="stage-permissions-tab-user"
          >
            👤 USER
          </button>
          <button
            type="button"
            onClick={() => {
              if (isDirty) {
                if (!window.confirm("Hai modifiche non salvate. Vuoi davvero cambiare tab e perderle?")) return;
              }
              setAudience("user_plus");
            }}
            className={`px-3 py-1.5 text-[11px] font-semibold rounded-lg transition-colors ${
              audience === "user_plus"
                ? "bg-violet-600 text-white shadow-sm"
                : "text-slate-600 hover:bg-slate-50"
            }`}
            data-testid="stage-permissions-tab-user-plus"
          >
            ⭐ USER+
          </button>
        </div>
        <div className="mt-3 flex items-center gap-4 text-[11px] text-slate-500 flex-wrap">
          <span>📊 {data.pipelines.length} pipeline · {totalStages} stage totali</span>
          {data.updated_at && (
            <span>
              Ultimo aggiornamento: {new Date(data.updated_at).toLocaleString("it-IT")}
              {data.updated_by && ` da ${data.updated_by}`}
            </span>
          )}
        </div>
      </div>

      <div className="px-5 py-4 space-y-3">
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-[11px] text-amber-900 leading-relaxed">
          <div className="font-semibold mb-1">Legenda:</div>
          <ul className="space-y-0.5 list-disc list-inside">
            <li><strong>VISIONE</strong>: l&apos;utente può aprire l&apos;opportunità in sola lettura (form bloccato).</li>
            <li><strong>MODIFICA</strong>: può modificare il piano di cura. Mutuamente esclusivo con VISIONE.</li>
            <li><strong>FOTO</strong>: può caricare/eliminare le foto. Indipendente — può combinarsi con VISIONE o MODIFICA.</li>
            <li>Selezionando FOTO mentre V e M sono entrambi spenti, VISIONE viene auto-attivata.</li>
            <li>Stage senza alcun flag → l&apos;opp resta visibile in lista ma cliccandola mostra &quot;Permesso negato&quot;.</li>
          </ul>
        </div>

        {data.pipelines.length === 0 && (
          <div className="text-center text-sm text-slate-500 py-8">Nessuna pipeline trovata in GHL</div>
        )}

        {data.pipelines.map((pipeline) => {
          const isOpen = expanded[pipeline.id] ?? true;
          const stages = pipeline.stages || [];
          return (
            <div
              key={pipeline.id}
              className="rounded-xl border border-slate-200 overflow-hidden"
              data-testid={`pipeline-${pipeline.id}`}
            >
              <button
                type="button"
                onClick={() => toggleExpanded(pipeline.id)}
                className="w-full px-4 py-3 bg-slate-50 hover:bg-slate-100 flex items-center justify-between transition-colors"
              >
                <div className="flex items-center gap-2 min-w-0">
                  {isOpen ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
                  <GitBranch className="w-4 h-4 text-indigo-500" />
                  <span className="text-sm font-semibold text-slate-800 truncate">{pipeline.name}</span>
                  <span className="text-[10px] text-slate-500 font-mono">({stages.length})</span>
                </div>
                <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => applyPipelinePreset(pipeline.id, "all")}
                    className="h-7 px-2 text-[10px] rounded-md border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    data-testid={`preset-all-${pipeline.id}`}
                  >
                    Tutto
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => applyPipelinePreset(pipeline.id, "vision")}
                    className="h-7 px-2 text-[10px] rounded-md border-blue-300 text-blue-700 hover:bg-blue-50"
                    data-testid={`preset-vision-${pipeline.id}`}
                  >
                    Solo visione
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => applyPipelinePreset(pipeline.id, "none")}
                    className="h-7 px-2 text-[10px] rounded-md border-red-300 text-red-700 hover:bg-red-50"
                    data-testid={`preset-none-${pipeline.id}`}
                  >
                    Niente
                  </Button>
                </div>
              </button>

              {isOpen && (
                <div className="bg-white">
                  <div className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-slate-100 bg-slate-50/50 text-[9px] font-bold uppercase tracking-wider text-slate-500">
                    <div className="col-span-6">Stage</div>
                    <div className="col-span-2 text-center">Visione</div>
                    <div className="col-span-2 text-center">Modifica</div>
                    <div className="col-span-2 text-center">Foto</div>
                  </div>
                  {stages.length === 0 ? (
                    <div className="px-4 py-4 text-[11px] text-slate-400 italic">Nessuno stage configurato</div>
                  ) : isPromemoriaPipeline(pipeline.name) ? (
                    // Pipeline Promemoria → riga unica "Tutti i mesi"
                    (() => {
                      const flags = getPromemoriaConsensus(pipeline.id, stages);
                      return (
                        <div
                          className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-slate-50 hover:bg-slate-50/50 transition-colors items-center"
                          data-testid={`stage-row-all-months-${pipeline.id}`}
                        >
                          <div className="col-span-6 text-xs text-slate-700 truncate font-semibold italic" title={`Tutti i ${stages.length} mesi`}>
                            Tutti i mesi <span className="text-[10px] text-slate-400 font-normal">({stages.length})</span>
                          </div>
                          <CheckboxCell
                            checked={flags.vision}
                            onChange={() => togglePromemoriaFlag(pipeline.id, stages, "vision")}
                            color="blue"
                            testid={`vision-all-${pipeline.id}`}
                          />
                          <CheckboxCell
                            checked={flags.edit}
                            onChange={() => togglePromemoriaFlag(pipeline.id, stages, "edit")}
                            color="emerald"
                            testid={`edit-all-${pipeline.id}`}
                          />
                          <CheckboxCell
                            checked={flags.photo}
                            onChange={() => togglePromemoriaFlag(pipeline.id, stages, "photo")}
                            color="amber"
                            testid={`photo-all-${pipeline.id}`}
                          />
                        </div>
                      );
                    })()
                  ) : (
                    stages.map((stage) => {
                      const flags = matrix[pipeline.id]?.[stage.id] || { vision: false, edit: false, photo: false };
                      return (
                        <div
                          key={stage.id}
                          className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-slate-50 hover:bg-slate-50/50 transition-colors items-center"
                          data-testid={`stage-row-${stage.id}`}
                        >
                          <div className="col-span-6 text-xs text-slate-700 truncate" title={stage.name}>
                            {stage.name}
                          </div>
                          <CheckboxCell
                            checked={flags.vision}
                            onChange={() => toggleFlag(pipeline.id, stage.id, "vision")}
                            color="blue"
                            testid={`vision-${stage.id}`}
                          />
                          <CheckboxCell
                            checked={flags.edit}
                            onChange={() => toggleFlag(pipeline.id, stage.id, "edit")}
                            color="emerald"
                            testid={`edit-${stage.id}`}
                          />
                          <CheckboxCell
                            checked={flags.photo}
                            onChange={() => toggleFlag(pipeline.id, stage.id, "photo")}
                            color="amber"
                            testid={`photo-${stage.id}`}
                          />
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const COLOR_MAP = {
  blue: "data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600",
  emerald: "data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600",
  amber: "data-[state=checked]:bg-amber-600 data-[state=checked]:border-amber-600",
};

function CheckboxCell({ checked, onChange, color, testid }) {
  return (
    <div className="col-span-2 flex justify-center">
      <Checkbox
        checked={checked}
        onCheckedChange={onChange}
        className={COLOR_MAP[color] || COLOR_MAP.blue}
        data-testid={testid}
      />
    </div>
  );
}
